import { ApiClient } from '../ApiClient';
import type { ApiResult } from '../types/api.types';
import { AiApi } from './AiApi';
import { AppApi } from './AppApi';
import { EntitiesApi } from './EntitiesApi';
import { FavoritesApi } from './FavoritesApi';
import { HelpersApi } from './HelpersApi';
import { IntranetApi } from './IntranetApi';
import { DatabaseApi } from './DatabaseApi';
import { EmailTestApi } from './EmailTestApi';
import { RightsApi } from './RightsApi';
import { SearchApi } from './SearchApi';
import { SessionsApi } from './SessionsApi';
import { UsersApi } from './UsersApi';

/**
 * Every call of the API v2, one class per backend module: `ApiService.entities.getPerson(1)`,
 * `ApiService.users.getUser(1)`. Failures come back as `ApiResult` with `isOk` false and the German messages in
 * `errors` — branch on their `inx`. A new module = its class next to these and one field here.
 */
class ApiServiceClass {
    public readonly ai: AiApi;
    public readonly app: AppApi;
    public readonly entities: EntitiesApi;
    public readonly favorites: FavoritesApi;
    public readonly helpers: HelpersApi;
    public readonly intranet: IntranetApi;
    public readonly database: DatabaseApi;
    public readonly emailTest: EmailTestApi;
    public readonly rights: RightsApi;
    public readonly search: SearchApi;
    public readonly sessions: SessionsApi;
    public readonly users: UsersApi;

    private readonly VersionPath = '/v2';
    private readonly Client = new ApiClient();

    public constructor() {
        this.ai = new AiApi(this.Client, this.VersionPath);
        this.app = new AppApi(this.Client, this.VersionPath);
        this.entities = new EntitiesApi(this.Client, this.VersionPath);
        this.favorites = new FavoritesApi(this.Client, this.VersionPath);
        this.helpers = new HelpersApi(this.Client, this.VersionPath);
        this.intranet = new IntranetApi(this.Client, this.VersionPath);
        this.database = new DatabaseApi(this.Client, this.VersionPath);
        this.emailTest = new EmailTestApi(this.Client, this.VersionPath);
        this.rights = new RightsApi(this.Client, this.VersionPath);
        this.search = new SearchApi(this.Client, this.VersionPath);
        this.sessions = new SessionsApi(this.Client, this.VersionPath);
        this.users = new UsersApi(this.Client, this.VersionPath);
    }

    /** The messages a user should read: every error except the bare HTTP reason (`inx` `API`). */
    public listErrorTexts(result: ApiResult<unknown>): string[] {
        const Texts = result.errors.filter((error): boolean => error.inx !== 'API').map((error): string => error.message);

        return Texts.length > 0 ? Texts : result.errors.map((error): string => error.message);
    }

    /** Whether two lists or records differ in any value (an address list in an editor). */
    public isChanged(before: unknown, after: unknown): boolean {
        return JSON.stringify(before) !== JSON.stringify(after);
    }

    /** The fields of `after` that differ from `before`, an emptied text as null — the body of a PATCH. */
    public listChanges<TData extends Record<string, string | boolean | null | undefined>>(before: TData, after: TData): Partial<TData> {
        const Changes: Partial<TData> = {};
        for (const Key of Object.keys(after) as (keyof TData)[]) {
            const Value = after[Key] === '' ? null : after[Key];
            if (Value !== (before[Key] === '' ? null : before[Key])) {
                Changes[Key] = Value as TData[keyof TData];
            }
        }

        return Changes;
    }
}

export const ApiService = new ApiServiceClass();
